export const settings = {
    title: "Settings",
    tabs: {
        general: "General",
        planUsage: "Plan & Usage",
        chat: "Chat",
        tasks: "Tasks",
        spotlight: "Genos",
        notifications: "Notifications",
        mentionGroups: "Mention groups",
        customEmoji: "Custom emoji",
        shortcuts: "Shortcuts",
        integrations: "Integrations",
        account: "Account",
        developer: "Developer",
    },
    mentionGroups: {
        heading: "Mention groups",
        description:
            "Team-scoped @groups. Anyone in your team can mention a group in chat, task comments, or task bodies; every member of the group is notified.",
        groupsHeading: "Groups",
        newGroup: "New group",
        namePlaceholder: "group-name (lowercase)",
        descriptionPlaceholder: "Description (optional)",
        empty: "No groups yet. Click + to create one.",
        selectPrompt: "Select a group on the left to manage its members.",
        modalHeading: "Mention group",
        notFound: "Group not found.",
        deleteGroup: "Delete group",
        deleteConfirm: "Delete @{name}? This cannot be undone.",
        addDescription: "Add a description…",
        membersCount: "Members ({count})",
        noMembers: "No members yet. Add some below.",
        removeMember: "Remove member",
        addMembers: "Add members",
        searchMembersPlaceholder: "Search team members…",
        noMatchingMembers: "No matching team members.",
        everyoneAlreadyMember: "Everyone is already a member.",
    },
    customEmoji: {
        heading: "Custom emoji",
        description:
            "Team-wide emoji, animated GIFs welcome. Type :name: in any editor, pick them from the emoji picker, or use them as reactions. Anyone on the team can add one; only the uploader can remove it.",
        chooseImage: "Choose image",
        namePlaceholder: "emoji-name (a-z, 0-9, _ + -)",
        addEmoji: "Add emoji",
        invalidFileType: "Use a .png, .jpg, .gif, .webp or .svg file.",
        fileTooLarge: "Emoji images must be 512 KB or smaller.",
        uploadFailed: "Upload failed — is the name already taken?",
        invalidName: "Names are 1-50 chars of a-z, 0-9, _, + or -.",
        empty: "No custom emoji yet. Add the first one!",
        deleteEmoji: "Delete (old messages keep it)",
    },
    developer: {
        keys: {
            title: "API keys",
            blurb: "Use a key to call the Genos API from your own scripts and tools. A key acts as you, so it can only see what you can see.",
            namePlaceholder: "What is this key for? e.g. CI bot",
            scopeRead: "Read",
            scopeWrite: "Read & write",
            create: "Create key",
            revoke: "Revoke",
            empty: "No API keys yet.",
            // Every key expires (default 90 days, at most a year), so the
            // picker has no "never" option.
            expiryLabel: "Expires after",
            expiryDays: "{count, plural, one {# day} other {# days}}",
            expiresOn: "Expires {date}",
            // Only keys made before expiry became mandatory say this.
            neverExpires: "Never expires — replace it with a new key",
            // Write keys only, off by default. It covers what can't be undone
            // (deleting a calendar event, a milestone, a sprint, a note or a
            // note folder) and what changes who has access (adding someone to
            // a project, or approving their request to join one, sharing a
            // note), and an instruction hidden in a task or a web page can ask
            // for any of it.
            allowDestructive:
                "Also allow actions that can't be undone or that change who has access (deleting calendar events, milestones, sprints, notes and note folders; adding people to projects or approving their requests to join; sharing notes)",
            destructiveChip: "Can delete",
        },
        // Owner-only. Lists every live key bound to the team, including
        // the ones editors made, which the list above never shows.
        teamKeys: {
            title: "Team API keys",
            blurb: "Every key that can act on this team, whoever made it. As the owner, you can revoke any of them. A key also stops working when its creator leaves the team.",
            empty: "No team keys right now.",
            createdBy: "Made by {name}",
            revokeNamed: "Revoke {name}",
        },
        // Owner-only, like the team keys. The team's audit trail: who
        // joined or left, role and ownership changes, invites, keys,
        // webhooks and alerts. A person's own sign-ins are not here.
        securityLog: {
            title: "Security log",
            blurb: "Sensitive changes to this team, newest first: members joining and leaving, role and ownership changes, invites, API keys, webhooks and alerts. Kept for a year.",
            empty: "Nothing recorded yet.",
            loadMore: "Show older",
            loadFailed: "Couldn't load the security log.",
            retry: "Try again",
            // "{actor} · {when}" under the action.
            byWhen: "{actor} · {when}",
            unknownActor: "Someone",
            sources: {
                app: "App",
                api_key: "API key",
                mcp: "MCP",
                agent: "Genos AI",
                system: "System",
                anonymous: "Signed out",
            },
            actions: {
                "signin.succeeded": "Signed in",
                "signin.failed": "Sign-in failed",
                signout: "Signed out",
                "password.reset_requested": "Password reset requested",
                "password.reset": "Password reset",
                "api_key.created": "API key created",
                "api_key.revoked": "API key revoked",
                "api_key.used": "API key used",
                "api_key.rejected": "API key rejected",
                "member.added": "Member added",
                "member.removed": "Member left",
                "role.changed": "Role changed",
                "owner.transferred": "Ownership transferred",
                "invite.sent": "Invite sent",
                "invite.accepted": "Invite accepted",
                "data.exported": "Data exported",
                "account.deleted": "Account deleted",
                "webhook.created": "Webhook created",
                "webhook.deleted": "Webhook deleted",
                "oauth.connected": "Account connected",
                "oauth.disconnected": "Account disconnected",
                "alert.export": "Alert: data exported",
                "alert.forbidden_burst": "Alert: many refused requests",
            },
        },
        docsLink: "Read the API docs →",
        webhooks: {
            title: "Webhooks",
            blurb: "Get an HTTPS request when something happens in this team. Each delivery is signed so you can verify it came from us.",
            urlPlaceholder: "https://your-app.example.com/genos",
            create: "Add webhook",
            delete: "Delete",
            empty: "No webhooks yet.",
            // The server disables an endpoint after 10 consecutive
            // failures; without saying so, "our webhooks stopped" is a
            // support ticket rather than something they can act on.
            disabled: "Disabled after repeated failures",
            managerOnly: "Only a team owner or editor can add a webhook.",
            createFailed: "Couldn't add that webhook. Please try again.",
            // The two scope pickers deliberately read differently,
            // because they behave differently: no project selected means
            // ALL projects (a filter left unset), while no channel
            // selected means NO chat (an allow-list left empty). Copy
            // that treated them alike would mislead in one direction or
            // the other.
            projectScopeAll: "Projects: all (select some to narrow)",
            projectScopeSome: "Projects: only the selected",
            channelScopeRequired:
                "Channels to watch — required. Message text is sent to your URL, so each channel has to be chosen. Direct messages are never eligible.",
            noEligibleChannels: "No group channels in this team yet.",
            chatNeedsChannels: "Choose at least one channel for chat events.",
        },
        // Both secrets are shown exactly once and cannot be recovered.
        // The copy has to say so before the panel is dismissed, or
        // people reasonably assume they can come back for it.
        oneShot: {
            title: "Copy your API key now",
            body: "This is the only time it will be shown. If you lose it, revoke this key and create another.",
            secretTitle: "Copy your signing secret now",
            secretBody:
                "Use it to verify the X-Genos-Signature header. This is the only time it will be shown.",
            copy: "Copy",
            dismiss: "I've saved it",
        },
    },
    // Settings → Account: GDPR export + erasure.
    account: {
        exportHeading: "Export your data",
        exportDescription:
            "Download everything you've created here — your profile, notes, todos, tasks, messages and comments — as a JSON file. Team content written by other people isn't included.",
        exportButton: "Download my data",
        // The person's own security log (MIP-745). The rows reuse the team
        // log's action and source labels (`developer.securityLog`).
        activityHeading: "Your security activity",
        activityDescription:
            "Your recent sign-ins, sign-outs, data exports, connected accounts and API keys, plus failed attempts to sign in to your account. For a failed attempt the address shown is the one the attempt came from, not yours. If something here wasn't you, change your password.",
        activityEmpty: "Nothing recorded yet.",
        activityLoadFailed: "Couldn't load your security activity.",
        activityLoadMore: "Show older",
        activityRetry: "Try again",
        teamsHeading: "Your teams",
        teamsDescription:
            "Leaving a team ends your access to its chats, projects and notes. Your account and your other teams are untouched, and someone can invite you back later.",
        teamsCurrent: "Current",
        teamsOwnerHint: "You own this team — transfer ownership from its profile before leaving.",
        teamsEmpty: "You don't belong to any team yet.",
        deleteHeading: "Delete your account",
        deleteDescription:
            "Permanently erase your account and remove you from every team you belong to. This cannot be undone — to leave a single team and keep the rest, use Your teams above.",
        deleteButton: "Delete account",
        ownershipBlocked:
            "You own teams that still have other members. Transfer ownership from each team's profile before deleting your account.",
        confirmHeading: "Delete your account?",
        confirmBody: "This is permanent. Here's exactly what happens:",
        confirmBullet1:
            "Your name, email and profile are erased, and you're signed out everywhere immediately.",
        confirmBullet2:
            "Your private notes, todos and AI history are deleted; teams where you were the last member are closed.",
        confirmBullet3:
            "Work you did with teammates stays in their workspace, attributed to “Deleted user”.",
        confirmTypeLabel: "Type DELETE to confirm",
        confirmPasswordLabel: "Your password",
        confirmDeleteButton: "Delete my account",
        cancel: "Cancel",
        errorPassword: "That password is incorrect.",
        errorOwnership: "Transfer ownership of your teams first.",
        errorGeneric: "Something went wrong. Please try again.",
    },
    planUsage: {
        heading: "Plan & Usage",
        description: "Your current plan and how much of each limit you've used.",
        viaTeam: "via team {team}",
        tierFree: "Free",
        tierCore: "Core",
        tierPro: "Pro",
        tierMax: "Max",
        tierEnterprise: "Enterprise",
        unlimited: "Unlimited",
        todaySuffix: "today",
        monthSuffix: "this month",
        // Reads "47 of 50 left this month". The bar beside it fills with
        // what REMAINS, so the number has to be the remainder too — the
        // old "3 / 50 this month" was a used-count, and next to a
        // nearly-full bar it said two opposite things at once. Matches
        // the phrasing `creditsRemaining` already uses.
        quotaRemaining: "{remaining} of {limit} left {window}",
        aiAsks: "AI asks",
        webSearches: "Web searches",
        tasksCreated: "Tasks created",
        notesCreated: "Notes created",
        messageHistory: "Message history",
        messageHistoryDays: "Last {days} days",
        maxFileSize: "Max file size",
        maxFileSizeMb: "{mb} MB per file",
        upgradeCta: "Upgrade plan",
        upgradeComingSoon:
            "Self-serve upgrades are coming soon. Contact your workspace admin to change plans.",
        loadError: "Couldn't load your plan details. Please try again.",
        upgradeToCore: "Upgrade to Core",
        upgradeToPro: "Upgrade to Pro",
        upgradeToMax: "Upgrade to Max",
        switchToCore: "Switch to Core",
        switchToPro: "Switch to Pro",
        switchToMax: "Switch to Max",
        cancelPlan: "Cancel plan",
        changeTeamPlan: "Change plan",
        planChangeHint:
            "Pick any plan below to switch — Stripe prorates the difference. Cancelling keeps your plan until the end of the current billing period.",
        manageBilling: "Manage billing",
        manageBillingHint: "Change plan, payment method, or cancel via the secure Stripe portal.",
        upgradeHint: "Checkout is handled securely by Stripe.",
        billingError: "Couldn't reach the billing service. Please try again.",
        billingReturnSuccess:
            "Thanks — your subscription is active! Your new plan applies within a few seconds.",
        planRenews: "Renews on",
        planEnds: "Plan ends on",
        cancelScheduled: "Cancellation is scheduled — your plan stays active until this date.",
        pastDue:
            "There's a payment problem — please update your payment method in the billing portal.",
        plansHeading: "Plans & pricing",
        plansSubheading:
            "Every plan includes every feature — plans differ only in usage limits and history.",
        freePrice: "Free",
        perMonth: "/ month",
        contactSales: "Contact sales",
        contactUs: "Contact us",
        currentPlan: "Current plan",
        yourPlanHeading: "Your plan",
        planSetByAdmin: "Set by your administrator",
        planSetByAdminHint:
            "This plan wasn't purchased through Stripe, so there's nothing to manage here. Contact your workspace admin or support to change it.",
        perDay: "{n} / day",
        perMonthCount: "{n} / month",
        premiumNote: "Daily caps for premium AI models also apply per plan.",
        comparePlans: "Compare plans",
        seePlans: "See plans",
        legalNotice: "Legal Notice",
        teamPlansHeading: "Team plan",
        teamPlansSubheading:
            "One payer, every member benefits — billed per seat. Only the team owner sees this section.",
        teamSeats: "{n} seats",
        teamUpgradeToCore: "Team Core",
        teamUpgradeToPro: "Team Pro",
        teamUpgradeToMax: "Team Max",
        manageTeamBilling: "Manage team billing",
        perSeatMonth: "{price} × {n} seats / month",
        plansHero: "Do more of your best work with Genos",
        plansHeroSub:
            "Start free and upgrade anytime. Every plan includes every feature — paid plans unlock more AI, unlimited history, and more room to create.",
        // Accessible name for the currency switcher. The switcher only
        // renders when more than one currency has prices configured.
        currencyLabel: "Display prices in",
        startForFree: "Start for free",
        taglineFree: "Get your chat, tasks, and notes in one place",
        taglineCore: "Unlimited history and daily AI",
        taglinePro: "Make AI part of your everyday work",
        taglineMax: "Full power for AI-heavy days",
        taglineEnterprise: "For teams that need it all",
        bestValue: "Best value",
        freeForever: "Free forever",
        benefitHistoryUnlimited: "Unlimited message history",
        benefitHistoryDays: "{days}-day message history",
        // --- AI allowance, two eras -------------------------------
        // Credits replaced daily ask/search counts as the customer's
        // limit. The plans page renders whichever the SERVER actually
        // enforces (it sends `monthly_ai_credits` only when credits
        // rule), so both sets stay until the daily era is retired.
        benefitAiAsks: "{n} AI asks every day",
        benefitAiAsksUnlimited: "Unlimited AI asks",
        benefitPremiumModels: "Premium AI models included",
        benefitWebSearches: "{n} AI web searches per day",
        benefitWebSearchesUnlimited: "Unlimited AI web searches",
        // Credits era. Web search is no longer a separate allowance —
        // it is priced into the request — so it gets no row of its own;
        // saying "included" is the honest summary.
        benefitAiCredits: "{n} AI credits every month",
        benefitAiCreditsUnlimited: "Unlimited AI credits",
        benefitCreditsExplainer: "Simple questions use fewer credits; deeper work uses more",
        benefitWebSearchIncluded: "Web search included",
        benefitTasks: "{n} tasks per month",
        benefitTasksUnlimited: "Unlimited tasks",
        benefitNotes: "{n} notes per month",
        benefitNotesUnlimited: "Unlimited notes",
        benefitUpload: "Files up to {mb} MB",
        // --- Plan comparison matrix -------------------------------
        // A feature is a ROW and a tier is a COLUMN, so the reader can
        // answer "what does Pro give me that Core doesn't?" by reading
        // across. That question is unanswerable from five separate
        // cards, which is why these are short values rather than whole
        // sentences — a cell has one column's width, and five sentences
        // side by side compare nothing.
        creditsPacksHeading: "Need more credits?",
        creditsPacksBlurb:
            "Buy a one-off pack. Your monthly credits are used first, and bought credits never expire.",
        creditsPackBuy: "Buy {n} credits",
        creditsPacksUnavailable: "Credit packs aren't available on your plan.",
        matrixHeading: "Compare every plan",
        matrixSub: "Every limit and capability, side by side.",
        matrixScrollHint: "Scroll sideways to see every plan.",
        // Read aloud in place of the tick/cross icons. A screen
        // reader on a row of bare glyphs learns nothing, and this
        // table is the whole page — so these are strings, not the
        // hardcoded English an icon label usually gets away with.
        matrixIncluded: "Included",
        matrixNotIncluded: "Not included",
        matrixFeature: "Feature",
        matrixUnlimited: "Unlimited",
        matrixForever: "Forever",
        matrixMb: "{mb} MB",
        matrixGroupAi: "Genos AI",
        matrixGroupReach: "What Genos can reach",
        matrixGroupWorkspace: "Your workspace",
        matrixRowCredits: "AI credits per month",
        matrixRowAsks: "AI asks per day",
        matrixRowAgency: "What Genos does",
        matrixRowDepth: "How deeply it thinks",
        matrixRowMemory: "What it draws on",
        matrixRowGenosHistory: "Genos conversation history",
        matrixRowPremiumModels: "Premium AI models",
        matrixRowDigest: "Proactive digest",
        matrixRowMcp: "Connect an AI coding agent (MCP)",
        matrixRowHistory: "Message history",
        matrixRowTasks: "Tasks per month",
        matrixRowNotes: "Notes per month",
        matrixRowUpload: "File upload size",
        // Short forms: inside a group already headed "What Genos can
        // reach", the word "integration" is noise in every row.
        matrixRowWeb: "Web search",
        matrixRowCalendar: "Google Calendar",
        matrixRowGithub: "GitHub",
        matrixAgencyRead: "Answers questions",
        matrixAgencyAct: "Takes actions",
        matrixAgencyOrganize: "Organizes whole plans",
        matrixDepthQuick: "Quick",
        matrixDepthThorough: "Thorough",
        matrixDepthDeep: "Deep",
        matrixDepthAdaptive: "Deep, auto-tuned",
        matrixMemoryNone: "This conversation",
        matrixMemoryOwn: "Your history",
        matrixMemoryTeam: "Your team's knowledge",
        matrixDigestWeekly: "Weekly",
        matrixDigestDaily: "Daily + alerts",
        billingReturnCancelled: "Checkout cancelled — your plan is unchanged.",
    },
    appearance: {
        heading: "Appearance",
        description:
            'Choose how the app looks. "System" follows your OS theme and updates automatically when it changes.',
        themeLabel: "Theme",
        themeHelper: "Applies immediately and persists across sessions.",
        themeLight: "Light",
        themeDark: "Dark",
        themeSystem: "System",
        colorThemeLabel: "Color theme",
        colorThemeHelper: "Changes the accent color across the whole app.",
        colorThemes: {
            purple: "Purple",
            blue: "Blue",
            teal: "Teal",
            emerald: "Emerald",
            amber: "Amber",
            rose: "Rose",
            slate: "Slate",
        },
    },
    messageLayout: {
        heading: "Message layout",
        description:
            "Bubble: flexible-width balloons aligned by author (current). Compact: full-width squared rows with author on the left (Slack-style).",
        styleLabel: "Style",
        styleHelper: "Applies immediately to chat, threads, and task comments.",
        styleBubble: "Bubble",
        styleCompact: "Compact",
    },
    sidebar: {
        heading: "Sidebar",
        description:
            "Choose which entries appear in the left sidebar. Genos is always shown. Syncs across your devices.",
        items: {
            inbox: "Inbox",
            chats: "Chats",
            tasks: "Tasks",
            notes: "Notes",
            todo: "To-Do",
        },
    },
    quickReactions: {
        heading: "Quick reactions",
        description:
            "The three one-click emoji offered when you hover a chat message, thread reply, or task comment.",
        label: "Emoji",
        helper: "Click a slot to change it. Team custom emoji can be used too.",
        slotTooltip: "Change this emoji",
        reset: "Reset",
    },
    messageActions: {
        heading: "Message actions",
        description:
            "Actions to keep on the hover toolbar of chat messages and thread replies instead of behind the … menu. Task comments are not affected.",
        limit: "Pick up to {max}. The rest stay in the … menu.",
        reset: "Reset",
    },
    quickAddRequiredFields: {
        heading: "Quick task creation",
        description:
            "How the inline quick-add row in the task table handles your project's required fields.",
        toggleLabel: "Require the project's required fields",
        toggleHelper:
            "Off by default, so a quick task needs only a title — the same as adding a sub-task. Turn this on to apply the project's creation rules to quick-add too. Configured default values are filled in either way, and the full create form always requires them.",
    },
    doubleClickTodo: {
        heading: "Double-click action",
        description: "Choose what happens when you double-click a message bubble.",
        toggleLabel: "Add message to To-Do on double-click",
        toggleHelper:
            "Turn this off to use double-click for selecting text instead. Applies to chat messages, thread messages, and task comments.",
    },
    spotlight: {
        heading: "Genos answers",
        description:
            "Spotlight search always looks through your chats, tasks, and notes. The switches below control whether Genos can write AI answers and browse the web.",
        aiAnswersLabel: "AI answers",
        aiAnswersHelper:
            "Lets the “Ask” button send your question to Genos. When off, Spotlight shows search results only.",
        webSearchLabel: "Web search",
        webSearchHelper:
            "Allow the agent to browse the web when answering. Requires AI answers to be on.",
    },
    // "What Genos remembers" — a sub-view of the Genos settings.
    //
    // Genos keeps no separate store of "memories". What it draws on is what
    // is listed here: the person's own conversations (earlier turns of the
    // current one, a rolling summary of older turns, and recall of past
    // conversations), their answers that teammates may see as anonymous
    // "Previous answer" cards, and the two settings it reads. The copy says
    // that plainly and avoids "memory" as a feature name for the same reason.
    genosMemory: {
        // The entry card shown in the Genos settings.
        entryHeading: "What Genos remembers",
        entryDescription:
            "See what Genos draws on when it answers you, delete your conversations, and choose what's shared with your team.",
        entryOpen: "Open",
        // Back to the Genos settings from the sub-view.
        backToSettings: "Genos settings",
        title: "What Genos remembers",
        intro: "Genos doesn't keep a separate store of “memories”. When it answers you, it draws on the three things below. You can see each one here and delete what you don't want kept.",
        conversations: {
            heading: "Your Genos conversations",
            description:
                "Genos reads the earlier turns of the conversation you're in, plus a short summary of turns older than that. It can also look back through your past conversations when a question needs it. All of this is built from questions you asked Genos, and deleting a conversation removes it all.",
            listNote: "This list shows your most recent conversations.",
            loading: "Loading conversations…",
            empty: "You have no Genos conversations in this team.",
            // The list request failed. Deliberately not `empty`: this page
            // must never tell someone they have no conversations because of
            // an outage.
            loadFailed: "Couldn't load your conversations.",
            deleteTooltip: "Delete this conversation",
            deleteAria: "Delete conversation: {question}",
            deleteTitle: "Delete this conversation?",
            deleteBody:
                "This permanently deletes the conversation and everything Genos remembers from it. This can't be undone.",
            deleteConfirm: "Delete conversation",
            deleteAll: "Delete all conversations",
            deleteAllTitle: "Delete all your Genos conversations?",
            deleteAllBody:
                "This permanently deletes every Genos conversation you have in this team, including ones started from a thread or a note and older ones this list doesn't show, along with everything Genos remembers from them. Your conversations in other teams are not affected. This can't be undone.",
            deleteAllAck: "I understand this can't be undone",
            deleteAllConfirm: "Delete everything",
            deletedAll: "All your Genos conversations in this team were deleted.",
            // The server refuses (and deletes nothing) while a conversation
            // is still answering.
            inFlight: "A conversation is still answering. Wait for it to finish, then try again.",
            failed: "Couldn't delete. Please try again.",
        },
        shared: {
            heading: "Answers shared with your team",
            switchLabel: "Let my answers help teammates",
            switchHelper:
                "When on, a completed first-turn answer may show to teammates as an anonymous “Previous answer” if they could see all of its sources. This applies in all your teams.",
            toggleFailed: "Couldn't change this setting. Please try again.",
            offTitle: "Stop sharing your answers?",
            offBody:
                "Your existing answers will be removed from your teams right away, and new ones won't be shared. The conversations themselves stay. If you turn sharing back on later, your recent answers are shared again shortly afterward. Answers you stopped sharing one by one stay stopped.",
            offConfirm: "Turn off sharing",
            loading: "Loading shared answers…",
            loadFailed: "Couldn't load your shared answers.",
            empty: "None of your answers are shared with teammates right now.",
            emptyOff: "Sharing is off, so none of your answers are shared.",
            // `audience_size` counts the person themself, so 1 means nobody
            // else; anything higher is "N people, including you".
            audienceOnlyYou: "Only you can see this",
            audienceMany: "Visible to {count} people, including you",
            stop: "Stop sharing this answer",
            stopTitle: "Stop sharing this answer?",
            stopBody:
                "Teammates will no longer see it as a “Previous answer”. The conversation stays in your history, and this answer won't be shared again.",
            stopConfirm: "Stop sharing",
            stopFailed: "Couldn't stop sharing that answer. Please try again.",
            hasMore:
                "Showing your latest {count} shared answers. There are more than this list shows.",
        },
        settingsUsed: {
            heading: "Settings Genos uses",
            description: "Genos reads these two settings when it answers you.",
            timezoneLabel: "Time zone",
            // The agent reads `CustomUser.timezone`, which the browser
            // reports on every boot. The profile card's location is a
            // separate column the agent does NOT read, so this deliberately
            // points at the device, not at the profile.
            timezoneHelper:
                "Used to work out what “today” and “this week” mean for you. It's detected from your device and updated automatically; to change it, change the time zone on your device.",
            timezoneUnknown: "Not detected yet",
            languageLabel: "Answer language",
            languageHelper:
                "Your app language is sent with each question so Genos can reply in it.",
            changeLanguage: "Change language",
            languagePath: "You can change it in {settings} › {tab} › {section}.",
        },
    },
    llmModel: {
        heading: "AI model",
        description:
            "Choose which LLM Genos uses to answer you. Each model has a separate daily quota that resets at UTC midnight.",
        providerLabel: "Provider",
        providerHelper: "Pick the AI service that should answer your asks.",
        modelLabel: "Model",
        modelHelper: "Models on the selected provider.",
        providerGemini: "Google Gemini",
        providerClaude: "Anthropic Claude",
        providerOpenai: "OpenAI GPT",
        // Shown beside a provider that is temporarily held back in
        // the picker (see HELD_BACK_PROVIDERS in SettingsModal).
        providerComingSoon: "Available soon",
        usageHeading: "Today's usage",
        usageUnlimited: "Unlimited",
        // Aggregate counters shown above per-model rows.
        llmAskLabel: "LLM asks",
        webSearchLabel: "Web searches",
        // Tier badge labels — Free / Pro / Max.
        tierFree: "Free",
        tierCore: "Core",
        tierPro: "Pro",
        tierMax: "Max",
        tierEnterprise: "Enterprise",
        upgradeNote: "Upgrade for higher daily limits on every model.",
        // --- Monthly AI credits (rendered when the backend serves a
        // `credits` block, i.e. credits are the authoritative limit).
        // These REPLACE the daily rows above rather than joining them:
        // showing both would present two limits when only one applies.
        // Always "AI credits", never a bare "credits" — on its own the
        // word raises the question it should answer (credits for what?).
        creditsHeading: "AI credits",
        creditsDescription:
            "Your monthly AI credits. Simple questions use fewer credits; deeper work uses more.",
        creditsRemaining: "{balance} of {limit} AI credits left",
        creditsUnlimited: "Unlimited AI credits on your plan.",
        creditsResets: "Resets {when}",
        creditsResetsToday: "Resets today",
        creditsResetsTomorrow: "Resets tomorrow",
        creditsResetsInDays: "Resets in {days} days",
        // Two distinct states, because the server now treats them
        // differently. Below one request's maximum you can still ask —
        // the run just stops partway if it turns out to be expensive.
        // At zero you cannot ask at all.
        // Shown under the meter when the user holds one-off credits.
        // Separate from the allowance because only one of the two
        // resets, and a single figure would imply both do.
        creditsPurchased: "+{n} bought credits — these don't expire",
        creditsLowWarning:
            "Running low — a long request may stop partway. Your AI credits reset {when}.",
        creditsEmptyWarning:
            "You're out of AI credits. They reset {when}, or upgrade your plan to keep going.",
        creditsUpgradeNote: "Upgrade for more AI credits each month.",
        noModelsConfigured: "No models are configured for your account.",
        // Effort levels (rendered when the backend serves `efforts`).
        // The user picks Low/Medium/High instead of a model; the notes
        // are the "diff of effort" they read to choose.
        effortLabel: "Effort",
        effortHelper: "How hard the AI works on each answer.",
        effortLow: "Low",
        effortMedium: "Medium",
        effortHigh: "High",
        effortLowNote: "Fastest — quick answers for simple questions.",
        effortMediumNote: "Balanced speed and quality — right for most questions.",
        effortHighNote: "Takes longer — deepest reasoning for complex questions.",
        // Rungs above the tier's ceiling render disabled with this hint
        // (the server would clamp them anyway — see max_effort).
        effortLockedHint: "Upgrade to unlock",
    },
    privacy: {
        heading: "Privacy & analytics",
        description:
            "Helps us understand which features get used so we can focus on the right things.",
        shareLabel: "Share usage data",
        shareHelper:
            "On by default. Sends your user ID, team ID and role plus the pages you visit, linked to your account. No message content, no clicks tracked. Turn off to opt out completely.",
    },
    shortcuts: {
        heading: "Keyboard shortcuts",
        global: {
            title: "Global",
            description:
                "Available anywhere. The letter shortcuts now compose navigation with a creation action; the cycle gesture works like the macOS Cmd+Tab switcher.",
            rows: {
                spotlight: "Open Spotlight search",
                tasksNew: "Open Tasks and start a new task",
                notesNew: "Open Notes and create a new My Note",
                calendar: "Open compact calendar",
                meetClipboard: "Copy a fresh Meet link to clipboard",
                openHistory: "Open history",
                openTaskDiagram: "Open task graph for the previewed task",
                openTodo: "Open the To-Do list",
                cycle: "Cycle through services",
            },
            cycleCombo: {
                hold: "Hold ⌘",
                tap: "Tap Ctrl to cycle",
                release: "Release ⌘ to switch",
            },
        },
        chat: {
            title: "Chat",
            description:
                "Active while a chat surface is mounted. Editable text fields keep platform text-selection shortcuts.",
            rows: {
                switchTab: "Switch chat tab",
                moveSelection: "Move selection in chat list",
                openThread: "Open thread of a message",
            },
            clickMessage: "Click message",
        },
    },
    language: {
        heading: "Language",
        description: "Choose the language used throughout the app.",
        label: "Language",
        helper: "Some translations may be incomplete; missing values fall back to English.",
        english: "English",
        japanese: "日本語",
        spanish: "Español",
        french: "Français",
        chinese: "中文",
        arabic: "العربية",
        hindi: "हिन्दी",
    },
    autoCloseOnPrMerge: {
        heading: "Auto-close on PR merge",
        description:
            "When a GitHub PR is merged, automatically close the task whose ID is in the PR's head branch name (e.g. branch `feature/GEN-42-foo` closes task GEN-42). Only fires for tasks you are assigned to. If the task has several PRs, it closes once the last open one merges.",
        toggleLabel: "Close my tasks when their PR merges",
        toggleHelper:
            "Disabled by default. Branch must follow the convention `…<TASK-ID>…` for the match to fire.",
    },
    autoSyncCalendar: {
        heading: "Auto-sync to Google Calendar",
        description:
            "Automatically add tasks with due dates to your Google Calendar as all-day events. One-way: deleting an event on Google won't affect your tasks.",
        toggleLabel: "Auto-sync task due dates",
        toggleHelper:
            "Off by default. Only tasks assigned to you sync. Past-due tasks are skipped on backfill.",
        connectPrompt: "Connect Google Calendar in Integrations to enable this.",
        grantPrompt: "Calendar access hasn't been granted yet — grant it to enable auto-sync.",
        grantButton: "Grant Calendar access",
        reconnectPrompt:
            "Your Google Calendar connection expired — reconnect to keep auto-sync working.",
        reconnectButton: "Reconnect Google Calendar",
        backfillButton: "Sync existing tasks now",
        backfillRunning: "Syncing…",
        backfillSuccess: "Synced {total} task(s) ({created} new, {patched} updated).",
        backfillSuccessWithCleared:
            "Synced {total} task(s) ({created} new, {patched} updated). Cleared {cleared} stale link(s) — events deleted on Google were NOT re-created.",
        backfillOnlyCleared:
            "Cleared {cleared} stale link(s) — events were deleted on Google. Per the never-re-create rule, no new events were posted.",
        backfillUpToDate: "Already up to date — no tasks needed syncing.",
        backfillFailed: "Backfill failed. Try again.",
    },
    taskSort: {
        heading: "Task sort",
        description:
            "Pick up to two sort keys for each surface. The primary key sorts first; the secondary key breaks ties. Applies immediately and persists across sessions.",
        sprintBoardLabel: "Board",
        sprintBoardHelper:
            "Cards within each column are sorted by these keys. Default leaves the order as the filter pipeline produced it.",
        tableLabel: "Task table",
        tableHelper:
            "Column-header clicks override the primary key (and toggle direction). The secondary key is preserved.",
        primaryLabel: "Primary",
        secondaryLabel: "Secondary",
        fieldNone: "None",
        directionAsc: "Ascending",
        directionDesc: "Descending",
    },
} as const;
